// src/utils/auditDiff.js
//
// What actually changed in an edit, for the audit log.
//
// Three screens independently logged the whole form payload on every save, so
// changing one phone number wrote fifty lines — the real change buried among
// forty-nine saying "[empty]". On the drivers page that payload includes pay
// rate and deposit balances, which meant an edit to somebody's phone number
// quietly copied their pay terms into a log every admin can read.
//
// The audit page has always known how to render `{ field: { oldValue,
// newValue } }` — it even resolves driver and truck ids to names. It was never
// being given that shape. This produces it.

// Bookkeeping that changes on every save. A log that reports `updatedAt`
// changed, every time, teaches people to stop reading the log.
const DEFAULT_IGNORED = [
  'updatedAt', 'createdAt', 'tenantId', 'id', 'docId',
  'updatedBy', 'createdBy', 'createdByEmail',
];

const isEmpty = (v) => v === null || v === undefined || v === '';

/**
 * Are these the same, as far as a human reading an audit log cares?
 *
 *  - null, undefined and "" all mean "not set", so moving between them is not
 *    a change worth a line.
 *  - Form inputs hand back strings, so 699 and "699" are the same number and
 *    must not be reported as an edit.
 *  - Objects and arrays compare by their JSON, which distinguishes "edited"
 *    from "untouched" without pulling in a deep-equality library.
 */
export function sameAuditValue(a, b) {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (typeof a === 'object' || typeof b === 'object') {
    try { return JSON.stringify(a ?? null) === JSON.stringify(b ?? null); }
    catch { return false; }
  }
  return String(a) === String(b);
}

/**
 * @param {object} before   the record as it was
 * @param {object} after    the fields being written
 * @param {string[]} [alsoIgnore]  extra fields to leave out
 * @returns {object} { field: { oldValue, newValue } } — only what changed
 */
export function diffForAudit(before, after, alsoIgnore = []) {
  const changes = {};
  if (!after) return changes;

  const ignored = new Set([...DEFAULT_IGNORED, ...alsoIgnore]);

  for (const field of Object.keys(after)) {
    if (ignored.has(field)) continue;

    const oldValue = before ? before[field] : undefined;
    const newValue = after[field];
    if (sameAuditValue(oldValue, newValue)) continue;

    changes[field] = {
      oldValue: oldValue === undefined ? null : oldValue,
      newValue: newValue === undefined ? null : newValue,
    };
  }

  return changes;
}

/** "3 fields changed" / "1 field changed" — for the one-line summary. */
export function describeChangeCount(changes) {
  const n = Object.keys(changes || {}).length;
  return n === 1 ? '1 field changed' : `${n} fields changed`;
}
